import * as React from 'react';
import { Tooltip } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { AssignedToDisplay } from '../deliverable_list_item/DeliverableListItem';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import { formatCurrency } from '../../utils/DeliverableUtils';
import { cn } from '../../utils/ClassNamesUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

// Read-only pieces of a template's deliverables and tasks, shared by the template
// detail page and the template form's deliverable list so the two read the same:
// "1. Name  (clock) 21 days · Depends on Deliverable 1  (avatars)", the description
// under it, then one line per task with its own time period and assignee.

const isBlank = (value) => value === undefined || value === null || value === '';

export const describeDays = (days) => `${days} ${Number(days) === 1 ? 'day' : 'days'}`;

// The 1-based position of the deliverable this one depends on, or null. Resolved by
// id, so it survives rows being added or removed.
export const getDependsOnNumber = (deliverable, deliverables): number | null => {
	const dependsOnId = nullSafeGet('dependsOnId', deliverable);
	if (!dependsOnId) {
		return null;
	}
	const position = (deliverables || []).findIndex(
		(candidate) => nullSafeGetOrElse('id', candidate, '') === dependsOnId
	);
	return position === -1 ? null : position + 1;
};

const TemplateDays: React.FC<{ days: any }> = ({ days }) =>
	isBlank(days) ? null : (
		<span className="bodyregular flex shrink-0 items-center gap-2xs text-character-secondary">
			<ClockCircleOutlined className="text-[12px]" />
			{describeDays(days)}
		</span>
	);

// One person reads as avatar + name; several collapse to overlapping avatars (hover
// any for the name and email), which is the only way a list fits on the line. The
// email is the fallback so an assignee whose contact no longer resolves (deactivated,
// out of scope) still shows instead of reading as unassigned.
const TemplateAssignees: React.FC<{ emails: string[] }> = ({ emails }) => {
	if (emails.length === 0) {
		return null;
	}
	if (emails.length === 1) {
		return (
			<span className="bodyregular shrink-0 text-character-primary">
				<UserNameWithEmail
					email={emails[0]}
					fallback={emails[0]}
					mode="inline"
					showNameWithAvatar
					size="small"
					placement="top"
					underline={false}
				/>
			</span>
		);
	}
	return <AssignedToDisplay emails={emails} />;
};

interface TemplateDeliverableHeadingProps {
	number: number;
	deliverable: any;
	// The deliverable this one depends on, by position and name, or null.
	dependsOnNumber: number | null;
	dependsOnName?: string | null;
	currency?: any;
	// Trailing controls (edit/delete on the form; nothing on the detail page).
	actions?: React.ReactNode;
}

export const TemplateDeliverableHeading: React.FC<TemplateDeliverableHeadingProps> = ({
	number,
	deliverable,
	dependsOnNumber,
	dependsOnName,
	currency,
	actions,
}) => {
	const budget = nullSafeGet('budget', deliverable);
	const description = nullSafeGet('description', deliverable);
	const formatBudget = (amount) =>
		currency && currency.symbolFormat ? currency.symbolFormat(amount, 0) : formatCurrency(amount);
	return (
		<>
			<div className="flex items-start gap-m">
				<div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-s gap-y-2xs">
					<span className="h5medium text-character-primary">
						{`${number}. ${nullSafeGetOrElse('name', deliverable, 'Untitled deliverable')}`}
					</span>
					<TemplateDays days={nullSafeGet('daysForDue', deliverable)} />
					{isBlank(budget) ? null : (
						<span className="bodyregular text-character-secondary">{`· ${formatBudget(
							budget
						)} budget`}</span>
					)}
					{dependsOnNumber ? (
						<Tooltip title={dependsOnName}>
							<span className="bodyregular text-character-secondary">{`· Depends on Deliverable ${dependsOnNumber}`}</span>
						</Tooltip>
					) : null}
					<TemplateAssignees emails={nullSafeGetOrElse('assignTo', deliverable, [])} />
				</div>
				{actions}
			</div>
			{description ? (
				<div className="bodyregular mt-2xs text-character-secondary">{description}</div>
			) : null}
		</>
	);
};

interface TemplateTaskLineProps {
	task: any;
	// e.g. "1.2" inside a deliverable on the form; omitted on the detail page.
	number?: string;
	className?: string;
}

export const TemplateTaskLine: React.FC<TemplateTaskLineProps> = ({ task, number, className }) => {
	const assignTo = nullSafeGet('assignTo', task);
	return (
		<div className={cn('flex flex-wrap items-center gap-x-s gap-y-2xs', className)}>
			{number ? (
				<span className="bodyregular w-8 shrink-0 text-character-secondary">{number}</span>
			) : null}
			<span className="bodyregular text-character-primary">{nullSafeGet('title', task)}</span>
			<TemplateDays days={nullSafeGet('daysForDue', task)} />
			{assignTo ? <TemplateAssignees emails={[assignTo]} /> : null}
		</div>
	);
};
